import { FileTagIconType } from '@/components/file-tag';
import {
  ImageMapArea,
  InboundAttachment,
} from '@/components/attachment-list/attachment-list-types';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { ListOption } from '@/components/list-item';
import { ListSection } from '@/components/recycler-list';
import {
  OrderInboundSource,
  SmartOrderProcessingStatus,
  SmartOrderSearchTermStatus,
  SmartOrderStatus,
} from '@/enums';
import type {
  OrderInboundListFieldsFragment as GeneratedOrderInboundListFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
  OrderInboundListSearchInputFieldsFragment,
  SearchFilterResponse,
  SmartOrderSearchInputsFieldsFragment,
  VariantPackOrderCount,
} from '@/types';

export type TranslationData = {
  original_detected_languages: string[];
  translation?: Record<string, string>;
} | null;

export type SearchInputFieldsFragmentWithTranslations = Omit<
  OrderInboundListSearchInputFieldsFragment,
  'translations' | 'search_term'
> & {
  search_term: Omit<
    OrderInboundListSearchInputFieldsFragment['search_term'],
    'translations'
  > & {
    translations?: null | {
      original_search_term: TranslationData;
    };
  };
  translations?: null | {
    matched_text?: TranslationData | null;
    original_pack_unit?: TranslationData | null;
    original_quantity?: TranslationData | null;
    requested_item_code?: TranslationData | null;
  };
};

export interface OrderInboundTranslations {
  search_text: TranslationData;
  subject: TranslationData;
}

export type OrderInboundListFieldsFragmentTranslations =
  OrderInboundTranslations | null;

export type OrderInboundListFieldsFragment = Omit<
  GeneratedOrderInboundListFieldsFragment,
  'attachments'
> & {
  attachments?: {
    attachment_list: AttachmentData[];
  } | null;
};

export interface SearchInput {
  itemNotes: string | null;
  matchedImageLocation?: MatchedImageLocation | null;
  matchedText: string | null;
  originalPackUnit?: string | null;
  originalQuantity?: number | null;
  packUnit: string | null;
  quantity: number;
  requestedItemCode?: string | null;
  requestedItemName?: string | null;
  result: SmartOrderSearchInputsFieldsFragment | null;
  searchTerm: string;
  status: SmartOrderSearchTermStatus;
  uuid: string;
}

export interface MatchedImageLocation {
  coords: [number, number][];
  url: string;
}

export interface VoicemailWordTimestamp {
  endSeconds: number | null;
  startSeconds: number;
  word: string;
}

export interface OrderMetadataSegment {
  attachmentIndex: number | null;
  matchedText: string | null;
  searchInput: SearchInput | null;
  smartOrderSearchInputUuid: string | null;
  sortOrder: number;
  startSeconds: number | null;
  translations: {
    matchedText?: TranslationData | null;
  } | null;
  uuid: string;
}

interface BaseTextSegment {
  originalMatchedText: string | null;
  text: string;
  timestamp: VoicemailWordTimestamp | null;
}

export type TextSegment =
  | (BaseTextSegment & { type: 'normal' })
  | (BaseTextSegment & { searchInputUUID: string; type: 'match' });

export interface EmployeeContactInfo {
  email?: string;
  name?: string;
  phone?: string;
}

export interface OrderInboundAssignedEmployee {
  name: string;
  uuid: string;
}

export interface OrderMetadata {
  assignedEmployee?: OrderInboundAssignedEmployee;
  attachments?: InboundAttachment[];
  createdAt: string;
  deliveryText?: string;
  employee?: EmployeeContactInfo;
  fulfillmentAt?: string;
  fulfillmentType?: string;
  fullDate: string;
  mergeUUID?: string;
  orderNotes?: string;
  originalFulfillmentAt?: string;
  originalSentAt?: string;
  poNumber?: string;
  processingStatus: SmartOrderProcessingStatus;
  restaurantName: string | null;
  restaurantUUID: string | null;
  searchInputs: SearchInput[];
  searchText?: string;
  segments?: OrderMetadataSegment[];
  smartOrderUUID: string;
  source: OrderInboundSource;
  status: SmartOrderStatus;
  subject?: string;
  submittedOrderNote?: string;
  submittedOrderUUID?: string;
  supplierUUID: string;
  threadId?: string;
  time: string;
  translations?: OrderInboundTranslations;
  uniqueEmployeeNames: string[];
  voicemailRecordingUrl?: string;
  voicemailWordTimestamps?: VoicemailWordTimestamp[];
  workflowMode: OrderAgentWorkflowMode;
}

export type UnknownContactInfo =
  | { emailAddress: string; phoneNumber?: never }
  | { emailAddress?: never; phoneNumber: string }
  | { emailAddress: string; phoneNumber: string };

export interface OrderInboundTag {
  iconType?: FileTagIconType;
  text: string;
}

export type OrderInboundOption = ListOption<{
  assignedEmployee?: OrderInboundAssignedEmployee;
  contactName?: string | null;
  hasAttachments: boolean;
  hasMatches: boolean;
  hasUnknownContact?: boolean;
  isProcessing: boolean;
  mergeUUID: string | null;
  numItems?: number;
  openOrderUUIDs: string[];
  orderUUIDs: string[];
  // Set on a REJECTED row. Replies in one email thread can be rejected in
  // separate batches, and each batch is its own list row, so this tells the
  // detail view which batch of the fetched thread the row stands for.
  rejectedUpdatedAt?: string;
  restaurantName: string | null;
  restaurantUUID: string | null;
  source: OrderInboundSource;
  submittedOrderUUID?: string;
  supplierUUID: string;
  tags: OrderInboundTag[];
  threadCount?: number;
  threadIds: string[];
  time: string;
  timestamp: string;
  unknownContactInfo?: UnknownContactInfo;
  workflowMode: OrderAgentWorkflowMode;
}>;

export interface OrderInboundOptionHeader {
  // If a location has multiple non-merged orders
  canMerge?: boolean;
  isFirstSection?: boolean;
  restaurantUUID: string | null;
}

export type OrderInboundSection = ListSection<
  OrderInboundOption,
  OrderInboundOptionHeader
>;

export enum AttachmentMimeType {
  AAC = 'audio/aac',
  AMR = 'audio/amr',
  CSV = 'text/csv',
  EML = 'message/rfc822',
  GIF = 'image/gif',
  HEIC = 'image/heic',
  HTML = 'text/html',
  JPG = 'image/jpeg',
  M4A = 'audio/m4a',
  MP3 = 'audio/mpeg',
  MP4_AUDIO = 'audio/mp4',
  OGG = 'audio/ogg',
  OPUS = 'audio/opus',
  PDF = 'application/pdf',
  PNG = 'image/png',
  TEXT = 'text/plain',
  THREE_GPP = 'audio/3gpp',
  UNKNOWN = 'application/octet-stream',
  WAV = 'audio/wav',
  WEBM_AUDIO = 'audio/webm',
  WEBP = 'image/webp',
  X_WAV = 'audio/x-wav',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export enum MimeTypeByExtension {
  csv = AttachmentMimeType.CSV,
  eml = AttachmentMimeType.EML,
  gif = AttachmentMimeType.GIF,
  heic = AttachmentMimeType.HEIC,
  html = AttachmentMimeType.HTML,
  jpeg = AttachmentMimeType.JPG,
  jpg = AttachmentMimeType.JPG,
  mp3 = AttachmentMimeType.MP3,
  pdf = AttachmentMimeType.PDF,
  png = AttachmentMimeType.PNG,
  DEFAULT = AttachmentMimeType.UNKNOWN,
}

export const AttachmentMimeTypeIcon: Record<
  AttachmentMimeType,
  FileTagIconType
> = {
  [AttachmentMimeType.AAC]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.AMR]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.CSV]: FileTagIconType.CSV,
  [AttachmentMimeType.EML]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.GIF]: FileTagIconType.IMAGE,
  [AttachmentMimeType.HEIC]: FileTagIconType.IMAGE,
  [AttachmentMimeType.HTML]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.JPG]: FileTagIconType.IMAGE,
  [AttachmentMimeType.M4A]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.MP3]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.MP4_AUDIO]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.OGG]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.OPUS]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.PDF]: FileTagIconType.PDF,
  [AttachmentMimeType.PNG]: FileTagIconType.IMAGE,
  [AttachmentMimeType.TEXT]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.THREE_GPP]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.UNKNOWN]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.WAV]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.WEBM_AUDIO]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.WEBP]: FileTagIconType.IMAGE,
  [AttachmentMimeType.X_WAV]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.XLS]: FileTagIconType.DEFAULT,
  [AttachmentMimeType.XLSX]: FileTagIconType.DEFAULT,
};

export type AttachmentFileExtension = keyof typeof MimeTypeByExtension;

export interface AttachmentData {
  is_for_parsing?: boolean;
  is_for_viewing?: boolean;
  is_inline_attachment?: boolean;
  mime_type?: AttachmentMimeType | null;
  parsed_text?: string | null;
  url: string;
}

export type VariantPackOrderCountsMap = Map<string, VariantPackOrderCount[]>;
export type SearchFiltersMap = Map<string, SearchFilterResponse>;
export type SwapSuggestionMap = Map<
  string,
  ItemsContext_VariantsPackFieldsFragment[]
>;
// Keyed by search input UUID, mirroring SwapSuggestionMap. Value is the
// replacement item a correction was applied with.
export type CorrectionPreviewMap = Map<string, ItemListOptionMetadata>;

export interface BaseInboundMetadataFields {
  inputWithResult: SearchInput;
  listOption: ItemListOption | null;
  matchTerm: string;
}

export type InboundListItem = ListOption<BaseInboundMetadataFields>;

export interface ZoomableImageMapProps {
  containerWidth: number;
  images: { areas: ImageMapArea[]; url: string }[];
  onAreaSelect: (area: ImageMapArea) => void;
  selectedAreaID?: string;
  setZoomLevel?: (level: number) => void;
  zoomControlsBottomOffset?: number;
  zoomLevel?: number;
}

export interface GetOrderSmartOrderMetadata {
  mergeUUID?: string;
  threadId?: string;
  uuid: string;
}

export enum OrderAgentWorkflowMode {
  Correction = 'CORRECTION',
  Submission = 'SUBMISSION',
}
